/**
 * 55 — the API client.
 *
 * Every endpoint in 04_API_SPEC.md, typed, plus one job the spec doesn't
 * mention but the band absolutely requires: turning machine failure into
 * English. See `humanError()` at the bottom. Trevor never sees a status code.
 */

/* ------------------------------------------------------------------ types */

export type Vote = -1 | 0 | 1;

export interface Member {
  id: string;
  name: string;
  color: string;
  role?: 'member' | 'admin';
}

export interface Votes {
  up: number;
  down: number;
  /** this member's vote: 1, -1, or 0/absent */
  mine?: Vote;
}

/** One person's reaction — on a take or on a note. `value` is which thumb. */
export interface Voter {
  member_id: string;
  name: string;
  /** 1 = up/loved, -1 = down */
  value: Vote;
}

/** The canonical version, as summarised on the shelf. */
export interface CanonicalRef {
  version_id: string;
  name: string;
  duration_s: number | null;
  recorded_at: number | null;
  votes?: Votes;
}

/** GET /api/songs — one row of the record shelf. */
export interface Song {
  id: string;
  title: string;
  slug: string;
  status?: string;
  color?: string | null;
  bpm?: number | null;
  key_sig?: string | null;
  canonical: CanonicalRef | null;
  take_count: number;
  version_count: number;
  latest_take_at: number | null;
  /** has a take this member has never played → the red dot */
  unheard?: boolean;
  /** Total band plays across this song's takes — the quiet listening widget on
   *  the shelf. Optional: absent until the Worker sends it. */
  play_count?: number;
  /** How many distinct members have heard it at all. */
  listener_count?: number;
  /** Lyric state for the tile glyph:
   *   'none'   — no words yet
   *   'has'    — words present, unlocked
   *   'review' — a take diverges from the locked master; Brad needs to vet
   *   'locked' — master frozen (padlock) */
  lyric_status?: 'none' | 'has' | 'review' | 'locked';
}

export interface Version {
  id: string;
  song_id: string;
  take_id: string;
  name: string;
  version_no?: number | null;
  variant?: string | null;
  notes?: string | null;
  is_archived?: boolean;
  duration_s: number | null;
  recorded_at: number | null;
  created_at?: number;
  created_by?: Member | null;
  device?: string | null; // 'zoom' | 'phone' | 'mixdown' ... — where it came from
  votes: Votes;
  /** who thumbed this take up/down — so the screen can show names, not just a count */
  voters?: Voter[];
  comment_count?: number;
  /** plays of THIS take alone (not the whole song) — shown on the take tile */
  play_count?: number;
  /** distinct members who've heard THIS take */
  listener_count?: number;
}

export interface Comment {
  id: string;
  version_id: string;
  /** null = a general note; 161.4 = "that ending at 2:41" */
  at_s: number | null;
  /** the END of a duration note: [at_s, end_s] is "this whole part of the song".
   *  null/absent = a single-moment (point) note. Always >= at_s when set. */
  end_s?: number | null;
  body: string;
  created_at: number;
  member: Member;
  /** ticked off — hidden from the wave unless "show done" is on */
  done?: boolean;
  /** reactions on this note — the {up,down,mine} tally for the current member */
  votes?: Votes;
  /** who reacted, and how — [{member_id,name,value}] */
  voters?: Voter[];
}

/** One member's listening history for a song (GET /api/songs/:id → listeners[]).
 *  The warm side of accountability: who's caught up, how much, and how recently. */
export interface SongListener {
  member_id: string;
  name: string;
  plays: number;
  /** unix seconds of their most recent play; null if the Worker didn't say. */
  last_at: number | null;
}

/** GET /api/songs/:id */
export interface SongDetail {
  song: Song;
  versions: Version[];
  comments: Comment[];
  me?: Member;
  /** Every take of this song, flat, longest first. The Worker groups these by
   *  session; the song screen doesn't use them, but the workbench does — and in
   *  there you aren't browsing a library, you're working through a pile. */
  takes: Take[];
  /** Who has heard this song — the "who's caught up" list on the song screen.
   *  Most-recently-heard first (getSong sorts). Absent until the Worker sends it. */
  listeners?: SongListener[];
  /** Total plays across the whole band — the section header ("9 plays"). */
  total_plays?: number;
}

/** GET /api/versions/:id/stream — a presigned R2 URL and its expiry. */
export interface StreamInfo {
  url: string;
  expires_at: number;
  peaks_url: string | null;
  duration_s: number | null;
  mime: string;
}

/** GET /api/takes/:id/mix — Tier 1 mix presets (12_STEMS_AND_LAYERS Part 3). */
export interface MixPreset {
  id: string;
  name: string;
  url: string;
  offset_s: number;
  duration_s: number | null;
  peaks_url?: string | null;
}
export type StemKind = 'track' | 'isolated' | 'minus' | 'full_mix' | 'overdub';

export interface MixLayer {
  role: string;
  label: string;
  /** The mono 32 kHz proxy. Deliberately small — this is what gets decoded. */
  url: string;
  /** The full-quality stem, when there is one. The DAW's Reaper export uses it. */
  proxy_url?: string | null;
  offset_s: number;
  stem_kind: StemKind;
  default_on: boolean;
  /** NULL = the Roadie hasn't aligned this one yet. The mixer stays hidden. */
  align_conf?: number | null;
  gain_db?: number;
}

/** Enough to title the DAW ("Into The Void · Apr 4") and to know where a
 *  mixdown belongs when it's sent back for the band to vote on. */
export interface MixTake {
  id: string;
  song_id: string | null;
  song_title: string | null;
  label: string | null;
  recorded_at: number | null;
}

export interface MixInfo {
  take: MixTake | null;
  duration_s: number | null;
  presets: MixPreset[];
  layers: MixLayer[];
  /** false until the Roadie has aligned the stems. A mixer that flams is
   *  worse than no mixer, so the UI stays hidden until this is true. */
  mixer_ready: boolean;
}

/** POST /api/uploads/presign */
export interface PresignRequest {
  filename: string;
  bytes: number;
  mime: string;
  song_id?: string | null;
  session_hint?: string;
  /** When a take is attached to a song, what was added on this pass: "Rob's
   *  keys", "Carmen's solo". The Worker uses it as the version's variant/label
   *  so overdub takes are named rather than "A version". */
  variant?: string | null;
}
export interface PresignSingle {
  mode: 'single';
  upload_id: string;
  url: string;
  r2_key: string;
  /** The exact Content-Type the Worker wants echoed on the PUT. Not signed, so a
   *  mismatch never breaks the upload — R2 just stores what we send. */
  headers?: Record<string, string>;
  expires_at?: number;
}
export interface PresignMultipart {
  mode: 'multipart';
  upload_id: string;
  r2_key: string;
  /** MUST be uniform and >= 5 MiB. R2 is stricter than S3 about this. */
  part_size: number;
  parts: { part: number; url: string }[];
  headers?: Record<string, string>;
  expires_at?: number;
}
export type PresignResponse = PresignSingle | PresignMultipart;

export interface CompleteResponse {
  source_file_id: string;
  /** present when the mime was already browser-playable — the Worker points a
   *  take straight at the raw upload, so it's audible to the band immediately. */
  take_id?: string | null;
  version_id?: string | null;
  playable?: boolean;
}

/* ------------------------------------------------------------------ errors */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Something we can put in front of a human being. */
  readonly human: string;

  constructor(status: number, code: string, serverMessage?: string) {
    super(serverMessage || code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.human = humanError(status, code, serverMessage);
  }
}

/**
 * The copy rules from 06_FRONTEND_SPEC, in code.
 *
 * Never show a UUID, a filename, a file size, a bitrate or an error code.
 * "That file's too big. Try a shorter clip?" — not "Upload failed: 413".
 *
 * The Worker already sends friendly `error` strings for the cases it knows
 * about, and we prefer those. This map is the safety net for everything else,
 * so that a route we haven't thought about still fails in English.
 */
function humanError(status: number, code: string, serverMessage?: string): string {
  switch (code) {
    case 'TOO_LARGE':
      return "That file's too big. Try a shorter clip?";
    case 'CAP_REACHED':
      return 'The vault is full. Ask Jonathan.';
    case 'BAD_TYPE':
      return 'Big master files go through Jonathan, not the phone. An MP3 or a voice memo is perfect.';
    case 'NOT_READY':
      return "That one's still landing. Give it a minute?";
    case 'FORBIDDEN':
      return "You're not on the list yet. Ask Jonathan.";
    case 'OFFLINE':
      return "You're offline. Nothing's broken — you just can't reach the vault right now.";
    case 'NETWORK':
      return 'Couldn’t reach the vault. Try again?';
    case 'NOT_FOUND':
      return "Couldn't find that one.";
  }

  // A message the Worker wrote for a human beats anything we can guess.
  if (serverMessage && !looksTechnical(serverMessage)) return serverMessage;

  if (status === 413) return "That file's too big. Try a shorter clip?";
  if (status === 415) return 'The app can’t play that kind of file. An MP3 or a voice memo works.';
  if (status === 403) return "You're not on the list yet. Ask Jonathan.";
  if (status === 404) return "Couldn't find that one.";
  if (status === 507) return 'The vault is full. Ask Jonathan.';
  if (status >= 500) return 'The vault had a wobble. Try again?';

  return 'Couldn’t reach the vault. Try again?';
}

/** Would this string make a non-technical person feel stupid? Then don't show it. */
function looksTechnical(s: string): boolean {
  return (
    /\b(error|exception|null|undefined|failed to fetch|status|http|sql|d1_|r2|uuid)\b/i.test(s) ||
    /[0-9a-f]{8}-[0-9a-f]{4}/i.test(s) ||
    /^\w+_\w+$/.test(s)
  );
}

/* ------------------------------------------------------------------ fetch */

/**
 * In production every request already carries a verified identity: it's behind
 * Cloudflare Access, which puts a signed JWT in a header the Worker reads.
 * There is no token for us to manage and no login screen for us to build.
 *
 * Access does NOT run against `wrangler dev`, so in dev only we tack on the
 * ?dev_email= param the Worker's dev-mode identity stub looks for. The Worker
 * guards that behind `ENVIRONMENT === 'dev'`.
 */
const DEV_EMAIL = 'hello@jonathanellis.ca';

function withDevIdentity(path: string): string {
  if (!import.meta.env.DEV) return path;
  const email =
    new URLSearchParams(location.search).get('dev_email') ??
    localStorage.getItem('55.dev_email') ??
    DEV_EMAIL;
  return path + (path.includes('?') ? '&' : '?') + 'dev_email=' + encodeURIComponent(email);
}

interface CallOptions {
  method?: string;
  body?: unknown;
  /** Phones on bad data retry a lot. Anything a retry could double up on
   *  carries this, and the Worker de-dupes on it. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function call<T>(path: string, opts: CallOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal } = opts;

  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let res: Response;
  try {
    res = await fetch(withDevIdentity('/api' + path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    // No response at all: the phone is on the train, or in the basement of a pub.
    throw new ApiError(0, navigator.onLine ? 'NETWORK' : 'OFFLINE');
  }

  if (!res.ok) {
    let code = 'HTTP_' + res.status;
    let msg: string | undefined;
    try {
      const j = (await res.json()) as { error?: string; code?: string };
      if (j.code) code = j.code;
      if (j.error) msg = j.error;
    } catch {
      /* a non-JSON error body is still an error; we just have less to go on */
    }
    throw new ApiError(res.status, code, msg);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** A key that's stable for one logical action but new on each fresh attempt. */
export function idemKey(): string {
  return crypto.randomUUID();
}

/* ----------------------------------------------------------- normalisation */
/*
 * The Worker is being built alongside this. Field names drift during a build,
 * and a phone in a pub is a bad place to discover that. Every read goes
 * through a normaliser: if a field is missing we degrade to a sane default
 * rather than throwing a TypeError into a black screen.
 */

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const MEMBER_FALLBACK: Member = { id: 'unknown', name: 'Someone', color: '#999999' };

function asMember(v: unknown): Member {
  if (!v || typeof v !== 'object') return MEMBER_FALLBACK;
  const o = v as Record<string, unknown>;
  return {
    id: String(o.id ?? 'unknown'),
    name: String(o.name ?? 'Someone'),
    color: typeof o.color === 'string' && o.color ? o.color : '#999999',
    role: o.role === 'admin' ? 'admin' : 'member',
  };
}

function asVotes(v: unknown): Votes {
  const o = (v ?? {}) as Record<string, unknown>;
  const mine = o.mine;
  return {
    up: Number(o.up ?? 0) || 0,
    down: Number(o.down ?? 0) || 0,
    mine: mine === 1 || mine === -1 ? (mine as Vote) : 0,
  };
}

/**
 * The voter list the Worker stitches onto a version or a comment:
 * [{member_id,name,value}]. A row with no thumb (value 0) or no member is
 * dropped — the screen only ever shows people who actually reacted.
 */
function asVoters(v: unknown): Voter[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      const value = o.value === 1 || o.value === -1 ? (o.value as Vote) : 0;
      return {
        member_id: String(o.member_id ?? o.id ?? ''),
        name: String(o.name ?? 'Someone'),
        value,
      };
    })
    .filter((x) => x.member_id && x.value !== 0);
}

function asSong(v: unknown): Song {
  const o = (v ?? {}) as Record<string, unknown>;
  const c = o.canonical as Record<string, unknown> | null | undefined;

  return {
    id: String(o.id ?? ''),
    title: String(o.title ?? 'Untitled'),
    slug: String(o.slug ?? ''),
    status: typeof o.status === 'string' ? o.status : undefined,
    color: typeof o.color === 'string' ? o.color : null,
    bpm: num(o.bpm),
    key_sig: typeof o.key_sig === 'string' ? o.key_sig : null,
    canonical:
      c && c.version_id
        ? {
            version_id: String(c.version_id),
            name: String(c.name ?? 'A version'),
            duration_s: num(c.duration_s),
            recorded_at: num(c.recorded_at),
            votes: asVotes(c.votes),
          }
        : null,
    take_count: Number(o.take_count ?? 0) || 0,
    version_count: Number(o.version_count ?? 0) || 0,
    latest_take_at: num(o.latest_take_at) ?? num(o.updated_at),
    unheard: o.unheard === true || o.unheard === 1,
    // Listening tallies for the shelf widget. Left undefined (not 0) when the
    // Worker omits them, so the widget can tell "no data" from "genuinely zero".
    play_count: num(o.play_count) ?? undefined,
    listener_count: num(o.listener_count) ?? undefined,
    lyric_status:
      o.lyric_status === 'has' || o.lyric_status === 'review' || o.lyric_status === 'locked'
        ? o.lyric_status
        : o.lyric_status === 'none'
          ? 'none'
          : undefined,
  };
}

function asVersion(v: unknown): Version {
  const o = (v ?? {}) as Record<string, unknown>;
  return {
    id: String(o.id ?? ''),
    song_id: String(o.song_id ?? ''),
    take_id: String(o.take_id ?? ''),
    name: String(o.name ?? 'A version'),
    version_no: num(o.version_no),
    variant: typeof o.variant === 'string' ? o.variant : null,
    notes: typeof o.notes === 'string' ? o.notes : null,
    is_archived: o.is_archived === 1 || o.is_archived === true,
    duration_s: num(o.duration_s),
    recorded_at: num(o.recorded_at),
    created_at: num(o.created_at) ?? undefined,
    created_by: o.created_by ? asMember(o.created_by) : null,
    device: typeof o.device === 'string' ? o.device : null,
    votes: asVotes(o.votes),
    voters: asVoters(o.voters),
    comment_count: Number(o.comment_count ?? 0) || 0,
    play_count: num(o.play_count) ?? undefined,
    listener_count: num(o.listener_count) ?? undefined,
  };
}

function asComment(v: unknown): Comment {
  const o = (v ?? {}) as Record<string, unknown>;
  // The Worker sends the author as flat member_id/member_name/member_color, not
  // a nested object — fold either shape into a Member.
  const member =
    o.member || o.author
      ? asMember(o.member ?? o.author)
      : asMember({ id: o.member_id, name: o.member_name, color: o.member_color });
  return {
    id: String(o.id ?? crypto.randomUUID()),
    version_id: String(o.version_id ?? ''),
    at_s: num(o.at_s),
    end_s: num(o.end_s),
    body: String(o.body ?? ''),
    created_at: num(o.created_at) ?? Math.floor(Date.now() / 1000),
    member,
    done: o.done === 1 || o.done === true,
    votes: asVotes(o.votes),
    voters: asVoters(o.voters),
  };
}

/* --------------------------------------------------------------- listening */

/** The shelf. One round trip; every song, most recently active first. */
export async function getSongs(signal?: AbortSignal): Promise<Song[]> {
  const r = await call<{ songs?: unknown[] }>('/songs', { signal });
  const songs = (r.songs ?? []).map(asSong).filter((s) => s.id);

  // Most recently active first. That is the entire sort. No filter chips.
  songs.sort((a, b) => (b.latest_take_at ?? 0) - (a.latest_take_at ?? 0));
  return songs;
}

/* ------------------------------------------------------------------- album */
/*
 * The record. There is one, for now, and it's called 2026. The home screen IS
 * this album — a sleeve front (the big disc, the title) and a sleeve back (the
 * numbered tracklist). The Worker returns the album's identity plus its tracks
 * as ordinary shelf rows that also carry a track number.
 */

/**
 * How the one record is stored. `compressed` keeps every track editable at a
 * fraction of the size; `lossless` is the biggest, at the best fidelity. It's
 * an admin choice, made in Settings, and read back here so the shelf and the
 * settings screen never disagree about it.
 */
export type Fidelity = 'compressed' | 'lossless';

export interface Album {
  id: string;
  title: string;
  subtitle: string;
  /** null until the Worker fills it in — the sleeve still paints without it. */
  year: number | null;
  track_count: number;
  /** compressed | lossless. Defaults to compressed until the Worker says. */
  fidelity: Fidelity;
  /** Minutes that fit on one side of the record — where Side A flips to Side B.
   *  Defaults to 20 so the flip maths works before anyone has set it. */
  side_minutes: number;
}

/** A shelf row that also knows where it sits on the sleeve back. */
export interface AlbumTrack extends Song {
  /** 1-based track number, or null if the Worker hasn't numbered it yet. */
  track_no: number | null;
}

/**
 * GET /api/album — the one record, 2026, and its tracks in sleeve order.
 *
 * Degrades like everything else here: the album's identity has sane defaults so
 * the sleeve front paints even when the payload is thin, and an empty tracklist
 * is a perfectly good answer, never a red error box.
 */
export async function getAlbum(
  signal?: AbortSignal,
): Promise<{ album: Album; tracks: AlbumTrack[] }> {
  const r = await call<Record<string, unknown>>('/album', { signal });
  const a = (r.album ?? {}) as Record<string, unknown>;

  const tracks: AlbumTrack[] = ((r.tracks as unknown[]) ?? [])
    .map((t) => {
      const o = (t ?? {}) as Record<string, unknown>;
      return { ...asSong(t), track_no: num(o.track_no) } as AlbumTrack;
    })
    .filter((t) => t.id);

  // Sleeve order: by track number. Anything unnumbered falls to the end rather
  // than leaping to the top on a null.
  tracks.sort((x, y) => (x.track_no ?? 9999) - (y.track_no ?? 9999));

  const sideM = num(a.side_minutes);
  const album: Album = {
    id: String(a.id ?? '2026'),
    title: String(a.title ?? '2026'),
    subtitle: String(a.subtitle ?? 'the writing sessions'),
    year: num(a.year),
    track_count: Number(a.track_count ?? 0) || tracks.length,
    fidelity: a.fidelity === 'lossless' ? 'lossless' : 'compressed',
    side_minutes: sideM != null && sideM > 0 ? sideM : 20,
  };

  return { album, tracks };
}

/* --------------------------------------------------------- just recorded */

/**
 * One clip on the "just recorded" surface. A jam captured on a phone shows up
 * here the moment it lands — playable straight away, and (until someone says
 * which song it is) waiting to be filed.
 *
 * `unfiled` is the whole point: true means it belongs to no song yet, so the
 * home screen must offer the one-tap "which song is this?".
 */
export interface RecentTake {
  take_id: string;
  song_id: string | null;
  song_title: string | null;
  song_slug: string | null;
  kind: string;
  duration_s: number | null;
  /** unix seconds */
  created_at: number | null;
  /** where it came from — a track role, 'phone'. Never shown to a human as-is. */
  device: string | null;
  /** who hit record. A name, never an id. */
  by_name: string | null;
  /** true = no song yet; it needs filing. */
  unfiled: boolean;
}

function asRecent(v: unknown): RecentTake {
  const o = (v ?? {}) as Record<string, unknown>;
  const songId = typeof o.song_id === 'string' && o.song_id ? o.song_id : null;
  return {
    take_id: String(o.take_id ?? ''),
    song_id: songId,
    song_title: typeof o.song_title === 'string' ? o.song_title : null,
    song_slug: typeof o.song_slug === 'string' ? o.song_slug : null,
    kind: String(o.kind ?? 'full'),
    duration_s: num(o.duration_s),
    created_at: num(o.created_at),
    device: typeof o.device === 'string' ? o.device : null,
    by_name: typeof o.by_name === 'string' && o.by_name ? o.by_name : null,
    // Trust the server flag, but fall back to "has no song" — a card must never
    // offer to file something that already belongs to a song.
    unfiled: songId ? false : o.unfiled !== false,
  };
}

/**
 * GET /api/recent — the just-recorded surface. Phone recordings, newest first,
 * already filtered server-side (no Zoom archive, no false starts). An empty
 * list is a perfectly good answer and never an error: the home screen simply
 * doesn't draw the section.
 */
export async function getRecent(signal?: AbortSignal): Promise<RecentTake[]> {
  const r = await call<{ takes?: unknown[] }>('/recent', { signal });
  return ((r.takes ?? []) as unknown[]).map(asRecent).filter((t) => t.take_id);
}

/**
 * POST /api/songs { title } — start a brand-new song so a just-recorded clip
 * can be filed straight onto it. Returns the new song's id (which is its slug).
 * Any member can do it — it's a band, not a bureaucracy.
 */
export async function createSong(
  title: string,
): Promise<{ id: string; slug: string; title: string }> {
  const r = await call<Record<string, unknown>>('/songs', {
    method: 'POST',
    body: { title },
    idempotencyKey: idemKey(),
  });
  return {
    id: String(r.id ?? ''),
    slug: String(r.slug ?? ''),
    title: String(r.title ?? title),
  };
}

/**
 * PATCH /api/album — the two things Settings can change about the record: how
 * it's stored (fidelity) and how long a side is (side_minutes). The Worker is
 * the authority — it's admin-only there — and the UI simply hides the controls
 * for everyone else. Send only what changed.
 */
export async function patchAlbum(patch: {
  fidelity?: Fidelity;
  side_minutes?: number;
}): Promise<void> {
  await call('/album', { method: 'PATCH', body: patch, idempotencyKey: idemKey() });
}

/**
 * POST /api/album/order — persist a drag-reorder as the record's sequence. Send
 * the song ids in their new top-to-bottom order; the Worker writes them back as
 * track_no 1..n. The home screen reorders optimistically and only calls this to
 * make it stick, so a failure here means "put the order back", nothing worse.
 */
export async function setAlbumOrder(order: string[]): Promise<void> {
  await call('/album/order', { method: 'POST', body: { order }, idempotencyKey: idemKey() });
}

/**
 * DELETE /api/songs/:id — take a song off the record. Nothing is destroyed
 * (ADR-010): the Worker shelves it, so every take, version and comment stays put
 * and it simply leaves the sleeve. The copy the band sees says exactly that.
 */
export async function deleteSong(songId: string): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}`, {
    method: 'DELETE',
    idempotencyKey: idemKey(),
  });
}

export async function getSong(id: string, signal?: AbortSignal): Promise<SongDetail> {
  const r = await call<Record<string, unknown>>(`/songs/${encodeURIComponent(id)}`, { signal });

  const song = asSong(r.song ?? r);
  const versions = ((r.versions as unknown[]) ?? [])
    .map(asVersion)
    .filter((v) => v.id && !v.is_archived);

  // Canonical pinned to the top; everything else newest first.
  const canonId = song.canonical?.version_id;
  versions.sort((a, b) => {
    if (a.id === canonId) return -1;
    if (b.id === canonId) return 1;
    return (b.recorded_at ?? b.created_at ?? 0) - (a.recorded_at ?? a.created_at ?? 0);
  });

  const comments = ((r.comments as unknown[]) ?? [])
    .map(asComment)
    .sort((a, b) => {
      // Timestamped notes in song order — that's how you read them back.
      // Undated general notes fall to the bottom, newest first.
      if (a.at_s == null && b.at_s == null) return b.created_at - a.created_at;
      if (a.at_s == null) return 1;
      if (b.at_s == null) return -1;
      return a.at_s - b.at_s;
    });

  // The Worker returns takes grouped into sessions. Flatten them, carrying the
  // session's label and date down onto each take — a take that doesn't know
  // when it was recorded is useless in the workbench.
  const takes: Take[] = [];
  for (const s of ((r.sessions as unknown[]) ?? []) as Record<string, unknown>[]) {
    for (const t of ((s.takes as unknown[]) ?? []) as Record<string, unknown>[]) {
      takes.push(
        asTake({
          ...t,
          song_id: song.id,
          session_label: s.label ?? null,
          recorded_at: s.recorded_at ?? null,
        }),
      );
    }
  }
  takes.sort((a, b) => b.duration_s - a.duration_s);

  // Who's heard this, with how much and how recently. Degrades like everything
  // else: a missing list is an empty list, never a thrown TypeError.
  const listeners: SongListener[] = (((r.listeners as unknown[]) ?? []) as unknown[])
    .map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      return {
        member_id: String(o.member_id ?? o.id ?? ''),
        name: String(o.name ?? 'Someone'),
        plays: Number(o.plays ?? 0) || 0,
        last_at: num(o.last_at),
      };
    })
    .filter((l) => l.member_id)
    // Most recently heard first — "who's caught up" reads newest at the top.
    .sort((a, b) => (b.last_at ?? 0) - (a.last_at ?? 0));

  const total_plays = num(r.total_plays) ?? listeners.reduce((n, l) => n + l.plays, 0);

  return { song, versions, comments, takes, listeners, total_plays, me: r.me ? asMember(r.me) : undefined };
}

/**
 * A presigned R2 URL, straight to the bytes. The <audio> element gets this and
 * Cloudflare never sees the request again. Good for an hour.
 * The player caches it for 55 minutes; do not call this on every seek.
 */
export async function getStream(versionId: string, signal?: AbortSignal): Promise<StreamInfo> {
  const r = await call<Record<string, unknown>>(
    `/versions/${encodeURIComponent(versionId)}/stream`,
    { signal },
  );
  return {
    url: String(r.url ?? ''),
    expires_at: num(r.expires_at) ?? Math.floor(Date.now() / 1000) + 3600,
    peaks_url: typeof r.peaks_url === 'string' ? r.peaks_url : null,
    duration_s: num(r.duration_s),
    mime: typeof r.mime === 'string' ? r.mime : 'audio/mpeg',
  };
}

/** 1 = up, -1 = down, 0 clears. Upserts on (version, member). */
export async function vote(versionId: string, value: Vote): Promise<void> {
  await call(`/versions/${encodeURIComponent(versionId)}/vote`, {
    method: 'POST',
    body: { value },
    idempotencyKey: idemKey(),
  });
}

/**
 * React to a note: 1 = agrees / good catch, -1 = disagrees, 0 clears. A straight
 * mirror of vote() one table over (note_votes), upserting on (comment, member).
 * The refreshed tally lands with the next getSong, so this just acks.
 */
export async function voteComment(commentId: string, value: Vote): Promise<void> {
  await call(`/comments/${encodeURIComponent(commentId)}/vote`, {
    method: 'POST',
    body: { value },
    idempotencyKey: idemKey(),
  });
}

/**
 * J6. "That ending at 2:41." at_s is optional; end_s turns a moment into a
 * duration note — "this whole part" — spanning [at_s, end_s].
 */
export async function addComment(
  versionId: string,
  body: string,
  at_s: number | null,
  end_s: number | null = null,
): Promise<Comment> {
  const r = await call<Record<string, unknown>>(
    `/versions/${encodeURIComponent(versionId)}/comments`,
    {
      method: 'POST',
      body: { body, at_s, end_s },
      idempotencyKey: idemKey(),
    },
  );
  return asComment(r.comment ?? r);
}

/**
 * Edit a note: rewrite it, slide it to a new mm:ss, stretch it over a part
 * (end_s), collapse it back to a moment (end_s: null), or tick it done.
 */
export async function editComment(
  id: string,
  patch: { body?: string; at_s?: number | null; end_s?: number | null; done?: boolean },
): Promise<void> {
  await call(`/comments/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

/** Delete a note outright. */
export async function deleteComment(id: string): Promise<void> {
  await call(`/comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** The "this is the one" button. Any member can press it. */
export async function setCanonical(songId: string, versionId: string): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}/canonical`, {
    method: 'POST',
    body: { version_id: versionId },
    idempotencyKey: idemKey(),
  });
}

export async function renameVersion(versionId: string, name: string): Promise<void> {
  await call(`/versions/${encodeURIComponent(versionId)}`, {
    method: 'PATCH',
    body: { name },
    idempotencyKey: idemKey(),
  });
}

/**
 * Edit a take's name, its date, and its "location" (device) in one go — the
 * fuller edit behind the ⋯ on a take. `recorded_at`/`device` land on per-version
 * overrides server-side, so fixing one take never rewrites its siblings; pass
 * null to reset a field back to auto-derived.
 */
export async function editVersion(
  versionId: string,
  patch: { name?: string; recorded_at?: number | null; device?: string | null },
): Promise<void> {
  await call(`/versions/${encodeURIComponent(versionId)}`, {
    method: 'PATCH',
    body: patch,
    idempotencyKey: idemKey(),
  });
}

/** Nothing is ever deleted. Archiving hides it; the bytes stay. */
export async function archiveVersion(versionId: string): Promise<void> {
  await call(`/versions/${encodeURIComponent(versionId)}`, {
    method: 'PATCH',
    body: { is_archived: 1 },
    idempotencyKey: idemKey(),
  });
}

/**
 * Tier 1 mix presets for a take. The Worker may not serve this yet, and the
 * Roadie may not have rendered anything — either way the answer is "no
 * presets", which is a perfectly good answer. Never let it break the song
 * screen.
 */
export async function getMix(takeId: string, signal?: AbortSignal): Promise<MixInfo> {
  try {
    const r = await call<Record<string, unknown>>(`/takes/${encodeURIComponent(takeId)}/mix`, {
      signal,
    });
    const presets = ((r.presets as unknown[]) ?? []).map((p) => {
      const o = p as Record<string, unknown>;
      return {
        id: String(o.id ?? ''),
        name: String(o.name ?? 'Mix'),
        url: String(o.url ?? ''),
        offset_s: Number(o.offset_s ?? 0) || 0,
        duration_s: num(o.duration_s),
        peaks_url: typeof o.peaks_url === 'string' ? o.peaks_url : null,
      } satisfies MixPreset;
    });
    const layers = ((r.layers as unknown[]) ?? []).map((l) => {
      const o = l as Record<string, unknown>;
      return {
        role: String(o.role ?? ''),
        label: String(o.label ?? o.role ?? 'Layer'),
        url: String(o.url ?? ''),
        proxy_url: typeof o.proxy_url === 'string' ? o.proxy_url : null,
        offset_s: Number(o.offset_s ?? 0) || 0,
        stem_kind: (o.stem_kind as MixLayer['stem_kind']) ?? 'track',
        default_on: o.default_on !== 0 && o.default_on !== false,
        align_conf: num(o.align_conf),
        gain_db: Number(o.gain_db ?? 0) || 0,
      } satisfies MixLayer;
    });

    const t = r.take as Record<string, unknown> | undefined;
    const take: MixTake | null = t
      ? {
          id: String(t.id ?? takeId),
          song_id: typeof t.song_id === 'string' ? t.song_id : null,
          song_title: typeof t.song_title === 'string' ? t.song_title : null,
          label: typeof t.label === 'string' ? t.label : null,
          recorded_at: num(t.recorded_at),
        }
      : null;

    return {
      take,
      duration_s: num(r.duration_s),
      presets,
      layers,
      mixer_ready: r.mixer_ready === true,
    };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) {
      return { take: null, duration_s: null, presets: [], layers: [], mixer_ready: false };
    }
    throw e;
  }
}

/* ---------------------------------------------------------------- lyrics */
/*
 * Auto-synced lyrics, the Apple-Music way. Timing is ALWAYS per-take: Whisper
 * transcribes a specific recording (word-level timestamps for THAT take), Brad's
 * clean master words are aligned to those timings, and the result is a stack of
 * timed lines. Takes vary — BPM, arrangement — so there is never one central
 * timing structure; each take carries its own.
 *
 * Every read here degrades to "nothing yet" rather than throwing, exactly like
 * the mix/daw/activity reads: a Worker that hasn't shipped these routes, or a
 * take nobody has transcribed, is a perfectly good answer and never a red box.
 */

/** One synced lyric line, timed to a SPECIFIC take. Seconds into that take. */
export interface LyricLine {
  idx: number;
  text: string;
  start_s: number;
  end_s: number;
}

/** Where a take's timed lines came from:
 *  'aligned' = Brad's clean master words mapped onto Whisper's per-take timings;
 *  'asr'     = raw Whisper transcript (no master to align to yet);
 *  'none'    = nothing timed for this take. */
export type LyricSource = 'aligned' | 'asr' | 'none';

/** GET /api/takes/:id/lyrics — the synced lines for one take. */
export interface TakeLyrics {
  lines: LyricLine[];
  source: LyricSource;
  /** Whisper has run on this take (raw words cached), so lines exist or can. */
  transcribed: boolean;
  /** The song has master lyrics (Brad has written the words). */
  has_master: boolean;
  /** The song's master is locked (frozen by Brad/Johhny). */
  locked?: boolean;
  /** Non-null when THIS take diverges from the locked master and awaits vetting. */
  flag?: { open: boolean; n_diff: number } | null;
}

/** GET /api/songs/:id/lyrics — the master words plus lock + vetting state. */
export interface SongLyrics {
  text: string | null;
  locked: boolean;
  locked_by: string | null;
  locked_by_name: string | null;
  locked_at: number | null;
  /** how many takes of this song are waiting to be vetted */
  open_reviews: number;
}

/** One item in the vetting queue (GET /api/lyrics/reviews) — a take whose ASR
 *  diverged from the locked master, with the machine-vs-master diff. */
export interface LyricReview {
  id: string;
  song_id: string;
  song_slug: string | null;
  song_title: string;
  take_id: string;
  version_id: string | null;
  version_name: string | null;
  recorded_at: number | null;
  n_diff: number;
  diff: { idx: number; master: string; heard: string; conf: number; start_s: number }[];
}

/** One hit from a lyric search — enough to jump to the song and seek the take. */
export interface LyricHit {
  song_id: string;
  song_title: string;
  take_id: string;
  /** the version whose take_id === take_id, when the Worker knows it */
  version_id: string | null;
  idx: number;
  /** the matched line, in full — the UI windows + highlights it */
  text: string;
  start_s: number;
  end_s: number | null;
}

/**
 * GET /api/songs/:id/lyrics — Brad's CLEAN master words (songs.lyrics). This is
 * the editable source of truth; the timed per-take lines are derived from it.
 * A missing route or a song with no lyrics both come back as { text: null }.
 */
export async function getSongLyrics(songId: string, signal?: AbortSignal): Promise<SongLyrics> {
  const empty: SongLyrics = {
    text: null,
    locked: false,
    locked_by: null,
    locked_by_name: null,
    locked_at: null,
    open_reviews: 0,
  };
  try {
    const r = await call<Record<string, unknown>>(
      `/songs/${encodeURIComponent(songId)}/lyrics`,
      { signal },
    );
    const raw = typeof r.lyrics === 'string' ? r.lyrics : typeof r.text === 'string' ? r.text : '';
    return {
      text: raw && raw.length ? raw : null,
      locked: r.locked === true || r.locked === 1,
      locked_by: typeof r.locked_by === 'string' ? r.locked_by : null,
      locked_by_name: typeof r.locked_by_name === 'string' ? r.locked_by_name : null,
      locked_at: num(r.locked_at),
      open_reviews: Number(r.open_reviews ?? 0) || 0,
    };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return empty;
    throw e;
  }
}

/** Freeze / release the master words. Owner-only server-side (Brad/Johhny). */
export async function setLyricsLock(songId: string, lock: boolean): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}/lyrics/${lock ? 'lock' : 'unlock'}`, {
    method: 'POST',
    idempotencyKey: idemKey(),
  });
}

/** The vetting queue for Brad/Johhny. Empty for everyone else (or 403 → []). */
export async function getLyricReviews(signal?: AbortSignal): Promise<LyricReview[]> {
  try {
    const r = await call<{ reviews?: unknown[] }>(`/lyrics/reviews`, { signal });
    return ((r.reviews as unknown[]) ?? []).map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      const diff = ((o.diff as unknown[]) ?? []).map((d) => {
        const p = (d ?? {}) as Record<string, unknown>;
        return {
          idx: Number(p.idx ?? 0) || 0,
          master: String(p.master ?? ''),
          heard: String(p.heard ?? ''),
          conf: Number(p.conf ?? 0) || 0,
          start_s: Number(p.start_s ?? 0) || 0,
        };
      });
      return {
        id: String(o.id ?? ''),
        song_id: String(o.song_id ?? ''),
        song_slug: typeof o.song_slug === 'string' ? o.song_slug : null,
        song_title: String(o.song_title ?? 'Untitled'),
        take_id: String(o.take_id ?? ''),
        version_id: typeof o.version_id === 'string' ? o.version_id : null,
        version_name: typeof o.version_name === 'string' ? o.version_name : null,
        recorded_at: num(o.recorded_at),
        n_diff: Number(o.n_diff ?? diff.length) || 0,
        diff,
      } satisfies LyricReview;
    }).filter((r) => r.id);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501 || e.status === 403)) return [];
    throw e;
  }
}

/** Rule on one vetting flag: 'hallucination' | 'hard_to_hear' | 'lyric_change'. */
export async function resolveLyricFlag(
  flagId: string,
  resolution: 'hallucination' | 'hard_to_hear' | 'lyric_change',
): Promise<void> {
  await call(`/lyrics/flags/${encodeURIComponent(flagId)}/resolve`, {
    method: 'POST',
    body: { resolution },
    idempotencyKey: idemKey(),
  });
}

/**
 * PUT /api/songs/:id/lyrics — save the clean master words. The Worker re-aligns
 * every transcribed take against the new words, so the synced view re-maps on
 * the next getTakeLyrics. Idempotent on the key so a phone that retries a save
 * doesn't stamp two writes.
 */
export async function putSongLyrics(songId: string, text: string): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}/lyrics`, {
    method: 'PUT',
    body: { lyrics: text },
    idempotencyKey: idemKey(),
  });
}

/**
 * GET /api/takes/:id/lyrics — the synced lines for THIS take, plus enough state
 * to say the right thing when there are none: is the take transcribed, does the
 * song even have master words yet.
 */
export async function getTakeLyrics(takeId: string, signal?: AbortSignal): Promise<TakeLyrics> {
  const empty: TakeLyrics = { lines: [], source: 'none', transcribed: false, has_master: false, locked: false, flag: null };
  try {
    const r = await call<Record<string, unknown>>(
      `/takes/${encodeURIComponent(takeId)}/lyrics`,
      { signal },
    );
    const lines: LyricLine[] = ((r.lines as unknown[]) ?? [])
      .map((x) => {
        const o = (x ?? {}) as Record<string, unknown>;
        const start = Number(o.start_s ?? 0) || 0;
        return {
          idx: Number(o.idx ?? 0) || 0,
          text: String(o.text ?? '').trim(),
          start_s: start,
          end_s: Number(o.end_s ?? start) || start,
        };
      })
      .filter((l) => l.text)
      // Song order: by index first, then by start time as a tiebreak.
      .sort((a, b) => a.idx - b.idx || a.start_s - b.start_s);

    const source: LyricSource =
      r.source === 'aligned' || r.source === 'asr' ? r.source : lines.length ? 'asr' : 'none';

    return {
      lines,
      source,
      transcribed:
        r.transcribed === true || source !== 'none' || Array.isArray(r.words) || lines.length > 0,
      has_master:
        r.has_master === true ||
        source === 'aligned' ||
        (typeof r.master === 'string' && r.master.trim().length > 0),
      locked: r.locked === true || r.locked === 1,
      flag:
        r.flag && typeof r.flag === 'object'
          ? { open: true, n_diff: Number((r.flag as Record<string, unknown>).n_diff ?? 0) || 0 }
          : null,
    };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return empty;
    throw e;
  }
}

/**
 * POST /api/takes/:id/transcribe — kick the Whisper job for one take. Admin-only
 * on the Worker; the UI only offers it to admins. Returns when the Worker has
 * accepted (or finished) the job; the caller then re-fetches getTakeLyrics.
 */
export async function transcribeTake(takeId: string): Promise<void> {
  await call(`/takes/${encodeURIComponent(takeId)}/transcribe`, {
    method: 'POST',
    idempotencyKey: idemKey(),
  });
}

/**
 * GET /api/lyrics/search?q= — find a line anywhere in the catalogue. Each hit
 * carries the song, the take, and where in that take the line lands, so the UI
 * can navigate there and seek. An empty query, a short one, or a Worker without
 * the route all return [].
 */
export async function searchLyrics(q: string, signal?: AbortSignal): Promise<LyricHit[]> {
  const query = q.trim();
  if (query.length < 2) return [];
  try {
    const r = await call<Record<string, unknown>>(
      `/lyrics/search?q=${encodeURIComponent(query)}`,
      { signal },
    );
    const arr = Array.isArray(r)
      ? r
      : ((r.results as unknown[]) ?? (r.hits as unknown[]) ?? []);
    return (arr as unknown[])
      .map((x) => {
        const o = (x ?? {}) as Record<string, unknown>;
        return {
          song_id: String(o.song_id ?? ''),
          song_title: String(o.song_title ?? o.title ?? 'Untitled'),
          take_id: String(o.take_id ?? ''),
          version_id: typeof o.version_id === 'string' ? o.version_id : null,
          idx: Number(o.idx ?? 0) || 0,
          text: String(o.text ?? o.line ?? '').trim(),
          start_s: Number(o.start_s ?? 0) || 0,
          end_s: num(o.end_s),
        } satisfies LyricHit;
      })
      .filter((h) => h.song_id && h.text);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/* --------------------------------------------------------------- uploading */

/**
 * Step 1 of the two-step presigned PUT. The bytes go browser → R2 directly;
 * they never pass through the Worker.
 *
 * This is also the money-safety valve (ADR-002): the Worker refuses here, up
 * front, before a single byte moves — too big, cap reached, or a master file
 * from a phone. Which means we can tell the user honestly *before* they sit
 * through an upload that was always going to fail.
 */
export function presignUpload(req: PresignRequest, signal?: AbortSignal): Promise<PresignResponse> {
  return call<PresignResponse>('/uploads/presign', {
    method: 'POST',
    body: req,
    idempotencyKey: idemKey(),
    signal,
  });
}

/**
 * Step 2. ETags are passed through VERBATIM — quotes and all. Strip them and
 * R2 answers InvalidPart, which is a genuinely miserable thing to debug.
 */
export function completeUpload(
  uploadId: string,
  parts: { part: number; etag: string }[] = [],
  durationS?: number | null,
): Promise<CompleteResponse> {
  return call<CompleteResponse>(`/uploads/${encodeURIComponent(uploadId)}/complete`, {
    method: 'POST',
    // duration_s lets the take show its real length immediately (a phone
    // recording knows how long it ran) instead of 0:00 until the Roadie runs.
    body: durationS && durationS > 0 ? { parts, duration_s: durationS } : { parts },
    idempotencyKey: 'complete:' + uploadId, // a retry must not create two rows
  });
}

/**
 * The whole presign → PUT → complete dance, for bytes we made ourselves rather
 * than bytes a person picked off their phone: a DAW overdub, a DAW mixdown.
 *
 * The upload screen has Uppy for files a human chose, with a dashboard and
 * pause/resume and all of it. This is the small, quiet path for a Blob that the
 * app generated — same three endpoints, same money-safety checks in the Worker,
 * no UI.
 *
 * ETags go back to R2 VERBATIM, quotes and all. Strip them and R2 answers
 * InvalidPart, which is a genuinely miserable thing to debug.
 */
export async function uploadBlob(
  blob: Blob,
  filename: string,
  opts: { songId?: string | null; durationS?: number | null; onProgress?: (f: number) => void } = {},
): Promise<CompleteResponse> {
  const pre = await presignUpload({
    filename,
    bytes: blob.size,
    mime: blob.type || 'audio/mpeg',
    song_id: opts.songId ?? null,
  });

  if (pre.mode === 'single') {
    const res = await fetch(pre.url, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': blob.type || 'audio/mpeg' },
    });
    if (!res.ok) throw new ApiError(res.status, 'PUT_FAILED');
    opts.onProgress?.(1);
    return completeUpload(pre.upload_id);
  }

  const parts: { part: number; etag: string }[] = [];
  let done = 0;
  for (const p of pre.parts) {
    const start = (p.part - 1) * pre.part_size;
    const chunk = blob.slice(start, Math.min(start + pre.part_size, blob.size));
    const res = await fetch(p.url, { method: 'PUT', body: chunk });
    if (!res.ok) throw new ApiError(res.status, 'PUT_FAILED');
    const etag = res.headers.get('etag');
    if (!etag) throw new ApiError(0, 'NO_ETAG');
    parts.push({ part: p.part, etag });
    done++;
    opts.onProgress?.(done / pre.parts.length);
  }
  return completeUpload(pre.upload_id, parts);
}

/* ------------------------------------------------------------- openDAW saves */
/*
 * The "studio save" round trip. openDAW (daw.thedevilssons.ca) is a full browser
 * DAW that only saves a project BUNDLE to the user's desktop — there is no cloud
 * save. The band passes a session around by hand: download the newest save here,
 * open it in openDAW, work, then upload the new save as the latest row for this
 * song. History is never lost — every upload is a NEW row (ADR-010).
 *
 * This is a DEDICATED path, separate from the audio uploads above. It keeps the
 * money-safety byte for byte (the 500 MB per-file cap, the 8 GB storage_ledger
 * cap, multipart for big bundles, retry-safe completion) but drops the audio-only
 * rules — a project bundle is any type, of any extension openDAW cares to save.
 */

/** One studio save on a song's shelf — what GET /api/daw/projects returns. */
export interface DawProject {
  id: string;
  /** null = a whole-session save, not tied to a single song. */
  song_id: string | null;
  title: string;
  /** The original human filename — what the download hands back. */
  filename: string;
  bytes: number;
  note: string | null;
  /** unix seconds */
  saved_at: number;
  /** Who saved it. name is null only when the member row has gone missing. */
  saved_by: { id: string | null; name: string | null };
}

function asDawProject(v: unknown): DawProject {
  const o = (v ?? {}) as Record<string, unknown>;
  const by = (o.saved_by ?? {}) as Record<string, unknown>;
  return {
    id: String(o.id ?? ''),
    song_id: typeof o.song_id === 'string' ? o.song_id : null,
    title: String(o.title ?? 'Studio save'),
    filename: String(o.filename ?? ''),
    bytes: Number(o.bytes ?? 0) || 0,
    note: typeof o.note === 'string' && o.note ? o.note : null,
    saved_at: num(o.saved_at) ?? 0,
    saved_by: {
      id: typeof by.id === 'string' ? by.id : null,
      name: typeof by.name === 'string' && by.name ? by.name : null,
    },
  };
}

/**
 * GET /api/daw/projects?song_id=<id> — this song's studio saves, newest first
 * (the Worker sorts). An empty list is a perfectly good answer and never an
 * error, and neither is a Worker that hasn't shipped this endpoint yet — the
 * studio section paints its explainer and upload button either way.
 */
export async function getDawProjects(songId: string, signal?: AbortSignal): Promise<DawProject[]> {
  try {
    const r = await call<unknown>(`/daw/projects?song_id=${encodeURIComponent(songId)}`, { signal });
    const arr = Array.isArray(r) ? r : ((r as { projects?: unknown[] }).projects ?? []);
    return arr.map(asDawProject).filter((p) => p.id);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

export interface DawPresignRequest {
  filename: string;
  bytes: number;
  song_id?: string | null;
  /** A bundle usually has no meaningful mime; the Worker defaults to octet-stream. */
  mime?: string;
  prefer_multipart?: boolean;
}

/**
 * POST /api/daw/presign — the money-safety valve for a project bundle. Same three
 * gates as the audio presign (too big, cap reached, too many parts), in the same
 * order, with the audio-only rules dropped. Returns the same single/multipart
 * plan shape the audio path uses.
 */
export function dawPresign(req: DawPresignRequest, signal?: AbortSignal): Promise<PresignResponse> {
  return call<PresignResponse>('/daw/presign', {
    method: 'POST',
    body: req,
    idempotencyKey: idemKey(),
    signal,
  });
}

export interface DawCompleteRequest {
  upload_id: string;
  title: string;
  song_id?: string | null;
  note?: string | null;
  /** Required for a multipart upload; ETags go back VERBATIM, quotes and all. */
  parts?: { part: number; etag: string }[];
}

/**
 * POST /api/daw/complete — finishes the upload, reads the TRUE size from R2, and
 * writes the daw_projects row. Retry-safe on the Worker (it hands back the same
 * project if the row is already done), so a dropped connection mid-response
 * replays cleanly instead of double-billing the ledger or making a second row.
 */
export async function dawComplete(req: DawCompleteRequest): Promise<DawProject> {
  const r = await call<Record<string, unknown>>('/daw/complete', {
    method: 'POST',
    body: req,
    idempotencyKey: 'daw-complete:' + req.upload_id,
  });
  return asDawProject(r);
}

/**
 * GET /api/daw/projects/:id/download — a 1-hour presigned R2 GET that a browser
 * SAVES (the Worker sets content-disposition: attachment under the clean human
 * filename). Returns just the URL; point an <a> or the location at it and the
 * file lands in Downloads, ready to open in openDAW. Never proxies the bytes.
 */
export async function dawDownloadUrl(projectId: string, signal?: AbortSignal): Promise<string> {
  const r = await call<Record<string, unknown>>(
    `/daw/projects/${encodeURIComponent(projectId)}/download`,
    { signal },
  );
  return String(r.url ?? '');
}

/**
 * One presigned PUT with a REAL progress bar. `fetch` can't report upload
 * progress, and a project bundle is tens to hundreds of MB, so this is XHR —
 * `upload.onprogress` gives genuine byte-level movement even on a single PUT
 * (the audio path's coarse per-part progress would sit at 0% for a 60 MB file).
 *
 * Resolves with the object's ETag (needed to complete a multipart upload). The
 * ETag is a CORS-exposed response header, exactly as the audio multipart path
 * already relies on. Content-Type is echoed but never signed, so a mismatch is
 * harmless.
 */
function dawPut(
  url: string,
  body: Blob,
  contentType: string | undefined,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);

    const onAbort = (): void => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    const cleanup = (): void => signal?.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (e): void => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = (): void => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve(xhr.getResponseHeader('etag'));
      } else {
        reject(new ApiError(xhr.status, 'PUT_FAILED'));
      }
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(new ApiError(0, navigator.onLine ? 'NETWORK' : 'OFFLINE'));
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    xhr.send(body);
  });
}

/**
 * The whole presign → PUT → complete dance for a saved openDAW project the user
 * just picked. Mirrors uploadBlob's single/multipart shape, but on the dedicated
 * DAW endpoints and with a genuine progress bar. `title` defaults to the song
 * name at the call site; `note` is optional ("what did you do?").
 */
export async function uploadDawFile(
  file: File,
  opts: {
    songId?: string | null;
    title: string;
    note?: string | null;
    onProgress?: (fraction: number) => void;
    signal?: AbortSignal;
  },
): Promise<DawProject> {
  const pre = await dawPresign(
    {
      filename: file.name,
      bytes: file.size,
      mime: file.type || 'application/octet-stream',
      song_id: opts.songId ?? null,
    },
    opts.signal,
  );

  const ct = pre.headers?.['Content-Type'] || file.type || 'application/octet-stream';

  if (pre.mode === 'single') {
    await dawPut(pre.url, file, ct, (f) => opts.onProgress?.(f), opts.signal);
    return dawComplete({
      upload_id: pre.upload_id,
      title: opts.title,
      song_id: opts.songId ?? null,
      note: opts.note ?? null,
    });
  }

  // Multipart: each part carries its OWN 0..1 progress; fold it into the whole so
  // the bar moves smoothly through the file rather than jumping one part at a time.
  const total = pre.parts.length;
  const parts: { part: number; etag: string }[] = [];
  for (const p of pre.parts) {
    const start = (p.part - 1) * pre.part_size;
    const chunk = file.slice(start, Math.min(start + pre.part_size, file.size));
    const done = parts.length;
    const etag = await dawPut(
      p.url,
      chunk,
      undefined, // the multipart upload's Content-Type was set at create time
      (f) => opts.onProgress?.((done + f) / total),
      opts.signal,
    );
    if (!etag) throw new ApiError(0, 'NO_ETAG');
    parts.push({ part: p.part, etag });
  }

  return dawComplete({
    upload_id: pre.upload_id,
    title: opts.title,
    song_id: opts.songId ?? null,
    note: opts.note ?? null,
    parts,
  });
}

/* -------------------------------------------------------------------- stems */

export interface StemRequest {
  source_file_id: string;
  /** Unique within the take. 'Tr1', 'room', 'overdub-1'. */
  role: string;
  stem_kind?: StemKind;
  label?: string;
  /** Where it sits on the take's timeline. */
  offset_s?: number;
  /** 0..1. An overdub cut in the browser is sample-locked by construction, so
   *  it is 1 — we know where the punch-in was, we didn't have to go find it. */
  align_conf?: number;
  gain_db?: number;
  default_on?: boolean;
}

/**
 * POST /api/takes/:id/stems — attach an uploaded file to a take as a layer.
 * (12_STEMS Part 6.) This is the last step of an overdub: the solo Rob just
 * played is now a layer of the Apr 4 jam, and everyone can hear it.
 */
export async function addStem(takeId: string, stem: StemRequest): Promise<void> {
  await call(`/takes/${encodeURIComponent(takeId)}/stems`, {
    method: 'POST',
    body: stem,
    idempotencyKey: idemKey(),
  });
}

/* --------------------------------------------------------------- workbench */
/*
 * ADR-005. The browser is a CUT-POINT EDITOR. Every function below sends
 * NUMBERS. Not one of them touches an audio byte. The Roadie runs
 * `ffmpeg -c copy` on the Mac — a stream copy: instant, lossless.
 *
 * Sending four numbers instead of 200 MB is not a compromise. It's better.
 */

export type TakeKind = 'full' | 'fragment' | 'false_start' | 'talking' | 'noodle';

/** One piece of a jam. Times are relative to the take on screen. */
export interface Segment {
  start_s: number;
  end_s: number;
  song_id?: string | null;
  kind: TakeKind;
  label?: string | null;
}

export interface Take {
  id: string;
  song_id: string | null;
  source_file_id: string;
  start_s: number;
  end_s: number;
  duration_s: number;
  kind: TakeKind;
  label: string | null;
  filename: string | null;
  auto_confidence: number | null;
  confirmed_by: string | null;
  confirmed_at: number | null;
  created_at: number;
  recorded_at?: number | null;
  session_label?: string | null;
  playable: boolean;
  has_peaks: boolean;
  /** only on the unsorted list, and only when we asked about a song */
  score?: number | null;
}

/** What the matcher thinks this take is. Never what it *knows*. */
export interface Candidate {
  song_id: string;
  title: string;
  color: string | null;
  /** 0..1 */
  score: number;
  rank: number;
  /** {"qmax":0.71,"tempo":0.9,"chords":0.6,"lyrics":0.8,"neighbour":1} */
  evidence: Record<string, number>;
}

export interface SongRef {
  id: string;
  title: string;
  color: string | null;
}

function asTake(v: unknown): Take {
  const o = (v ?? {}) as Record<string, unknown>;
  const start = Number(o.start_s ?? 0) || 0;
  const end = Number(o.end_s ?? 0) || 0;
  const kind = String(o.kind ?? 'full') as TakeKind;
  return {
    id: String(o.id ?? ''),
    song_id: typeof o.song_id === 'string' ? o.song_id : null,
    source_file_id: String(o.source_file_id ?? ''),
    start_s: start,
    end_s: end,
    duration_s: num(o.duration_s) ?? Math.max(0, end - start),
    kind,
    label: typeof o.label === 'string' ? o.label : null,
    filename: typeof o.filename === 'string' ? o.filename : null,
    auto_confidence: num(o.auto_confidence),
    confirmed_by: typeof o.confirmed_by === 'string' ? o.confirmed_by : null,
    confirmed_at: num(o.confirmed_at),
    created_at: num(o.created_at) ?? 0,
    recorded_at: num(o.recorded_at),
    session_label: typeof o.session_label === 'string' ? o.session_label : null,
    playable: o.playable === 1 || o.playable === true,
    has_peaks: o.has_peaks === 1 || o.has_peaks === true,
    score: num(o.score),
  };
}

function asCandidate(v: unknown): Candidate {
  const o = (v ?? {}) as Record<string, unknown>;
  let evidence: Record<string, number> = {};
  // The Roadie writes this as a JSON string. If it ever writes an object
  // instead, take that too rather than showing an empty panel.
  if (typeof o.evidence === 'string') {
    try {
      evidence = JSON.parse(o.evidence) as Record<string, number>;
    } catch {
      /* unparseable evidence is not worth breaking a screen over */
    }
  } else if (o.evidence && typeof o.evidence === 'object') {
    evidence = o.evidence as Record<string, number>;
  }
  return {
    song_id: String(o.song_id ?? ''),
    title: String(o.title ?? 'Untitled'),
    color: typeof o.color === 'string' ? o.color : null,
    score: Math.max(0, Math.min(1, Number(o.score ?? 0) || 0)),
    rank: Number(o.rank ?? 99) || 99,
    evidence,
  };
}

/** A take's own stream. A fresh phone upload has no version row, so
 *  /versions/:id/stream can never reach it — the workbench edits TAKES. */
export async function getTakeStream(takeId: string, signal?: AbortSignal): Promise<StreamInfo> {
  const r = await call<Record<string, unknown>>(`/takes/${encodeURIComponent(takeId)}/stream`, {
    signal,
  });
  return {
    url: String(r.url ?? ''),
    expires_at: num(r.expires_at) ?? Math.floor(Date.now() / 1000) + 3600,
    peaks_url: typeof r.peaks_url === 'string' ? r.peaks_url : null,
    duration_s: num(r.duration_s),
    mime: typeof r.mime === 'string' ? r.mime : 'audio/mpeg',
  };
}

/** The "which song is this?" panel, in one round trip. */
export async function getCandidates(
  takeId: string,
  signal?: AbortSignal,
): Promise<{ take: Take; candidates: Candidate[]; songs: SongRef[] }> {
  const r = await call<Record<string, unknown>>(
    `/takes/${encodeURIComponent(takeId)}/candidates`,
    { signal },
  );
  return {
    take: asTake(r.take),
    candidates: ((r.candidates as unknown[]) ?? [])
      .map(asCandidate)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 5),
    songs: ((r.songs as unknown[]) ?? []).map((s) => {
      const o = s as Record<string, unknown>;
      return {
        id: String(o.id ?? ''),
        title: String(o.title ?? 'Untitled'),
        color: typeof o.color === 'string' ? o.color : null,
      };
    }),
  };
}

/** "Not sure which song this is — help?" Ranked against this song, if given. */
export async function getUnsortedTakes(songId?: string, signal?: AbortSignal): Promise<Take[]> {
  const q = songId ? `?song_id=${encodeURIComponent(songId)}` : '';
  const r = await call<{ takes?: unknown[] }>(`/takes/unsorted${q}`, { signal });
  return ((r.takes ?? []) as unknown[]).map(asTake).filter((t) => t.id);
}

/**
 * Cut a jam into pieces. Sends `segments`; gets back new take ids.
 *
 * The original take is untouched and stays exactly where it was. This is the
 * whole point: an edit is a new row, never a mutation. (ADR-010)
 */
export async function splitTake(
  takeId: string,
  segments: Segment[],
): Promise<{ take_ids: string[]; job_id: string }> {
  return call(`/takes/${encodeURIComponent(takeId)}/split`, {
    method: 'POST',
    body: { segments },
    idempotencyKey: idemKey(),
  });
}

/** Keep this bit. Makes a NEW take; never mutates the original. */
export async function trimTake(
  takeId: string,
  start_s: number,
  end_s: number,
  label?: string | null,
): Promise<{ take_id: string; job_id: string }> {
  return call(`/takes/${encodeURIComponent(takeId)}/trim`, {
    method: 'POST',
    body: { start_s, end_s, label: label ?? null },
    idempotencyKey: idemKey(),
  });
}

/**
 * ONE TAP. And it's the tap that makes the whole matcher work.
 *
 * A confirmed take joins the reference bank, and the matcher scores new jams
 * against the reference bank — same room, same amps, same arrangement — not
 * against a studio master it shares no landmarks with. Cold start is 60-75%
 * top-1; after a few confirmations per song, 85-95%. (ADR-008)
 */
export async function confirmTake(takeId: string, songId: string): Promise<{ version_id: string | null }> {
  const r = await call<{ version_id?: string | null }>(`/takes/${encodeURIComponent(takeId)}/confirm`, {
    method: 'POST',
    body: { song_id: songId },
    idempotencyKey: idemKey(),
  });
  return { version_id: r.version_id ?? null };
}

/** What the upload flow's auto-analyze returns: the transcript, rough timed
 *  lines, and the song guess — everything the "confirm your upload" step needs.
 *  `too_big`/`failed` mean it couldn't read the words (a huge WAV master); the
 *  take still landed and plays, you just don't get lyrics from it. */
export interface AnalyzeResult {
  take_id: string;
  transcribed: boolean;
  too_big?: boolean;
  failed?: boolean;
  current_song_id: string | null;
  transcript: string;
  lines: { idx: number; text: string; start_s: number; end_s: number }[];
  guessed: { song_id: string; slug: string; title: string; score: number } | null;
}

/** Transcribe a just-uploaded take and guess its song. Any member. */
export async function analyzeTake(takeId: string): Promise<AnalyzeResult> {
  const r = await call<Record<string, unknown>>(`/takes/${encodeURIComponent(takeId)}/analyze`, {
    method: 'POST',
    idempotencyKey: idemKey(),
  });
  const g = r.guessed as Record<string, unknown> | null | undefined;
  return {
    take_id: String(r.take_id ?? takeId),
    transcribed: r.transcribed === true,
    too_big: r.too_big === true,
    failed: r.failed === true,
    current_song_id: typeof r.current_song_id === 'string' ? r.current_song_id : null,
    transcript: String(r.transcript ?? ''),
    lines: ((r.lines as unknown[]) ?? []).map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      return {
        idx: Number(o.idx ?? 0) || 0,
        text: String(o.text ?? '').trim(),
        start_s: Number(o.start_s ?? 0) || 0,
        end_s: Number(o.end_s ?? 0) || 0,
      };
    }),
    guessed: g && g.song_id
      ? { song_id: String(g.song_id), slug: String(g.slug ?? ''), title: String(g.title ?? 'a song'), score: Number(g.score ?? 0) || 0 }
      : null,
  };
}

/** Scrap a bungled recording for good — deletes the take and reclaims its bytes. */
export async function scrapTake(takeId: string): Promise<void> {
  await call(`/takes/${encodeURIComponent(takeId)}`, { method: 'DELETE' });
}

/** Take → version. The copy says "Save this as a version", never "promote". */
export async function promoteTake(
  takeId: string,
  name: string,
  extra: { version_no?: number | null; variant?: string | null } = {},
): Promise<{ version_id: string; is_canonical: boolean }> {
  return call(`/takes/${encodeURIComponent(takeId)}/promote`, {
    method: 'POST',
    body: { name, ...extra },
    idempotencyKey: idemKey(),
  });
}

export async function patchTake(
  takeId: string,
  patch: { label?: string | null; kind?: TakeKind; song_id?: string | null },
): Promise<void> {
  await call(`/takes/${encodeURIComponent(takeId)}`, {
    method: 'PATCH',
    body: patch,
    idempotencyKey: idemKey(),
  });
}

export async function patchSong(
  songId: string,
  patch: {
    title?: string;
    status?: string;
    bpm?: number | null;
    key_sig?: string | null;
    color?: string | null;
    notes?: string | null;
  },
): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}`, {
    method: 'PATCH',
    body: patch,
    idempotencyKey: idemKey(),
  });
}

/** Two spellings of the same song were always one song. The loser's title
 *  becomes an alias — the matcher reads aliases, so it stays useful. */
export async function mergeSong(songId: string, intoSongId: string): Promise<void> {
  await call(`/songs/${encodeURIComponent(songId)}/merge`, {
    method: 'POST',
    body: { into_song_id: intoSongId },
    idempotencyKey: idemKey(),
  });
}

/* ------------------------------------------------------------------- peaks */

/**
 * Peaks are pre-computed by `audiowaveform` on the Mac and stored in R2. We
 * fetch the JSON. We never, ever decode audio on the phone to draw a
 * waveform — a 20-minute file would be a 30-second stall and possibly an OOM.
 *
 * `audiowaveform --output-format json` gives:
 *   { version, channels, sample_rate, samples_per_pixel, bits, length, data }
 * where `data` is interleaved min,max pairs of signed ints at `bits` depth.
 *
 * wavesurfer wants one array per channel, normalised to -1..1. Passing the
 * interleaved min/max run straight through is exactly right: it draws the
 * envelope, at double resolution, for free.
 */
export async function getPeaks(url: string, signal?: AbortSignal): Promise<number[][] | null> {
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const j = (await res.json()) as unknown;
    return normalisePeaks(j);
  } catch {
    // No peaks is not an error. It means the Roadie hasn't run yet, and the
    // song screen says so in words. It never blocks playback.
    return null;
  }
}

export function normalisePeaks(j: unknown): number[][] | null {
  if (!j) return null;

  // Already in wavesurfer's shape: [[...], [...]]
  if (Array.isArray(j) && Array.isArray(j[0])) return j as number[][];

  // A bare array of samples.
  if (Array.isArray(j) && typeof j[0] === 'number') return [j as number[]];

  const o = j as Record<string, unknown>;

  if (Array.isArray(o.peaks)) return normalisePeaks(o.peaks);

  if (Array.isArray(o.data)) {
    const data = o.data as number[];
    const bits = Number(o.bits ?? 8);
    const channels = Math.max(1, Number(o.channels ?? 1));
    const scale = Math.pow(2, bits - 1); // 8-bit → 128, 16-bit → 32768

    if (channels === 1) {
      const out = new Array<number>(data.length);
      for (let i = 0; i < data.length; i++) out[i] = (data[i] ?? 0) / scale;
      return [out];
    }

    // Interleaved across channels: [c0min, c0max, c1min, c1max, ...]
    const out: number[][] = Array.from({ length: channels }, () => []);
    for (let i = 0; i < data.length; i += 2 * channels) {
      for (let c = 0; c < channels; c++) {
        out[c]!.push((data[i + 2 * c] ?? 0) / scale, (data[i + 2 * c + 1] ?? 0) / scale);
      }
    }
    return out;
  }

  return null;
}

/* ---------------------------------------------------------------- the room */
/*
 * "Can we see who has logged in and listened too? Who is seeing things?"
 *
 * Six kinds of thing happen in this app and they all end up here. The Worker
 * sends FACTS — a kind, a member, a song, a timestamp. It does not send
 * sentences: "Rob listened to Cast Your Stones" is a UI string, and UI strings
 * that arrive from a database can never be re-worded without a migration.
 * activity.ts turns these into English.
 *
 * Note what is NOT in this payload, and keep it that way: no IP addresses, no
 * user agents, no failed logins, no play counts. This is the band seeing each
 * other, not a manager reading a log. (The security audit view is a different
 * endpoint, /api/admin/activity, and it is Jonathan-only.)
 */

export type ActivityKind = 'login' | 'play' | 'comment' | 'vote' | 'version' | 'take';

export interface ActivityItem {
  kind: ActivityKind;
  /** null for a take — a take has no author, it fell out of a recorder. */
  member: Member | null;
  song_id: string | null;
  song_title: string | null;
  version_id: string | null;
  take_id: string | null;
  /** unix seconds */
  at: number;
  /** a comment's body, a version's name, a take's label — whatever it had to say */
  detail: string | null;
  /** WHERE in the recording, for a comment. Not when it was said. */
  at_s: number | null;
  /** a vote: 1 = loved it, -1 = not feeling it */
  value: number | null;
}

export interface Presence {
  id: string;
  name: string;
  color: string;
  /** null = has never been in */
  last_seen_at: number | null;
}

export interface SeenBy {
  member: Member;
  played_at: number;
}

function asActivity(v: unknown): ActivityItem {
  const o = (v ?? {}) as Record<string, unknown>;
  const memberId = typeof o.member_id === 'string' ? o.member_id : null;

  return {
    kind: String(o.kind ?? 'take') as ActivityKind,
    // A row with no member is a fact about a take, not about a person. Don't
    // invent "Someone" here — the sentence for it doesn't have a name in it.
    member: memberId
      ? {
          id: memberId,
          name: String(o.member_name ?? 'Someone'),
          color: typeof o.member_color === 'string' && o.member_color ? o.member_color : '#999999',
        }
      : null,
    song_id: typeof o.song_id === 'string' ? o.song_id : null,
    song_title: typeof o.song_title === 'string' ? o.song_title : null,
    version_id: typeof o.version_id === 'string' ? o.version_id : null,
    take_id: typeof o.take_id === 'string' ? o.take_id : null,
    at: num(o.at) ?? 0,
    detail: typeof o.detail === 'string' ? o.detail : null,
    at_s: num(o.at_s),
    value: num(o.value),
  };
}

/**
 * The feed. Newest first, already merged and sorted by the Worker in one query.
 *
 * An empty feed is a perfectly good answer and so is a Worker that hasn't been
 * deployed with this endpoint yet: neither is worth a red error box on a screen
 * whose entire job is to feel like a room with people in it.
 */
export async function getActivity(limit = 60, signal?: AbortSignal): Promise<ActivityItem[]> {
  try {
    const r = await call<{ activity?: unknown[] }>(`/activity?limit=${limit}`, { signal });
    return (r.activity ?? []).map(asActivity).filter((a) => a.at > 0);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/* -------------------------------------------------- Johhny's dashboards */

/** One song's listeners (GET /api/admin/dashboard → songs[]). */
export interface DashSong {
  song_id: string;
  title: string;
  track_no: number | null;
  total_plays: number;
  listeners: { member_id: string; name: string; plays: number; last_at: number }[];
}

/** One member's engagement rollup (GET /api/admin/dashboard → members[]). */
export interface DashMember {
  member_id: string;
  name: string;
  color: string;
  role: string;
  last_seen_at: number | null;
  songs_heard: number;
  total_plays: number;
  comments: number;
  reactions: number;
  last_plays: { title: string; at: number }[];
  score: number;
}

export interface Dashboard {
  song_total: number;
  songs: DashSong[];
  members: DashMember[];
}

/**
 * GET /api/admin/dashboard — the totals behind Johhny's three dashboards.
 * Admin-only server-side; anyone else (or a Worker without the route) gets an
 * empty dashboard rather than an error, so the Activity screen never red-boxes.
 */
export async function getDashboard(signal?: AbortSignal): Promise<Dashboard> {
  const empty: Dashboard = { song_total: 0, songs: [], members: [] };
  try {
    const r = await call<Record<string, unknown>>('/admin/dashboard', { signal });
    const songs = ((r.songs as unknown[]) ?? []).map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      return {
        song_id: String(o.song_id ?? ''),
        title: String(o.title ?? 'Untitled'),
        track_no: num(o.track_no),
        total_plays: Number(o.total_plays ?? 0) || 0,
        listeners: ((o.listeners as unknown[]) ?? []).map((l) => {
          const p = (l ?? {}) as Record<string, unknown>;
          return {
            member_id: String(p.member_id ?? ''),
            name: String(p.name ?? 'Someone'),
            plays: Number(p.plays ?? 0) || 0,
            last_at: Number(p.last_at ?? 0) || 0,
          };
        }),
      } satisfies DashSong;
    });
    const members = ((r.members as unknown[]) ?? []).map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      return {
        member_id: String(o.member_id ?? ''),
        name: String(o.name ?? 'Someone'),
        color: typeof o.color === 'string' && o.color ? o.color : '#999999',
        role: String(o.role ?? 'member'),
        last_seen_at: num(o.last_seen_at),
        songs_heard: Number(o.songs_heard ?? 0) || 0,
        total_plays: Number(o.total_plays ?? 0) || 0,
        comments: Number(o.comments ?? 0) || 0,
        reactions: Number(o.reactions ?? 0) || 0,
        last_plays: ((o.last_plays as unknown[]) ?? []).map((l) => {
          const p = (l ?? {}) as Record<string, unknown>;
          return { title: String(p.title ?? 'a take'), at: Number(p.at ?? 0) || 0 };
        }),
        score: Number(o.score ?? 0) || 0,
      } satisfies DashMember;
    });
    return { song_total: Number(r.song_total ?? songs.length) || 0, songs, members };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501 || e.status === 403)) return empty;
    throw e;
  }
}

/** Who is around. Five rows, always — including the ones who have never been in. */
export async function getPresence(signal?: AbortSignal): Promise<Presence[]> {
  try {
    const r = await call<{ presence?: unknown[] }>('/presence', { signal });
    return (r.presence ?? []).map((v) => {
      const o = (v ?? {}) as Record<string, unknown>;
      return {
        id: String(o.member_id ?? o.id ?? ''),
        name: String(o.name ?? 'Someone'),
        color: typeof o.color === 'string' && o.color ? o.color : '#999999',
        last_seen_at: num(o.last_seen_at),
      };
    }).filter((p) => p.id);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/** The coloured dots under a take: who has actually heard this one. */
export async function getSeenBy(takeId: string, signal?: AbortSignal): Promise<SeenBy[]> {
  try {
    const r = await call<{ seen_by?: unknown[] }>(
      `/takes/${encodeURIComponent(takeId)}/seen`,
      { signal },
    );
    return (r.seen_by ?? []).map((v) => {
      const o = (v ?? {}) as Record<string, unknown>;
      return {
        member: asMember({ id: o.member_id, name: o.name, color: o.color }),
        played_at: num(o.played_at) ?? 0,
      };
    });
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/**
 * GET /api/auth/members — the public band roster (the same list the login gate
 * reads). The song screen uses it to know who has NOT heard a song yet, so the
 * "who's caught up" list can show the whole band, not only the ones who've
 * listened. An empty list — or a Worker without the route — is a fine answer:
 * the section simply shows whoever the listeners payload already named.
 */
export async function getMembers(signal?: AbortSignal): Promise<Member[]> {
  try {
    const r = await call<unknown>('/auth/members', { signal });
    const arr = Array.isArray(r) ? r : ((r as { members?: unknown[] } | null)?.members ?? []);
    return (arr as unknown[]).map(asMember).filter((m) => m.id && m.id !== 'unknown');
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/**
 * Presence heartbeat. main.ts pings this every ~30s ONLY while the tab is
 * visible. It is the one thing that makes "here now" honest: stop pinging
 * (close/background the app) and you drop offline within ~75s. Fire-and-forget
 * — a missed beat is not worth a toast.
 */
export async function heartbeat(): Promise<void> {
  try {
    await call<{ ok: boolean }>('/heartbeat', { method: 'POST' });
  } catch {
    /* offline or logged out — the next beat (or the login gate) handles it */
  }
}

/**
 * POST /api/auth/logout — drop the session cookie. The caller reloads into the
 * login gate the moment this returns; there is nothing to render here, so a 204
 * is success. It's allowed to throw — a logout the network couldn't confirm is
 * still handled by the caller reloading, which is better than trapping someone
 * on a screen they're trying to leave.
 */
export async function logout(): Promise<void> {
  await call('/auth/logout', { method: 'POST' });
}
